// Collection Context Management
// Handles storing and retrieving collection context for breadcrumb navigation

class CollectionContextManager {
  constructor() {
    this.storageKey = 'productCollectionContext'
    this.maxAge = 30 * 60 * 1000 // 30 minutes
  }

  // Store collection context when navigating from collection to product
  storeContext(handle, title, url) {
    try {
      const context = {
        handle: handle,
        title: title,
        url: url,
        timestamp: Date.now(),
      }
      sessionStorage.setItem(this.storageKey, JSON.stringify(context))

      // Also add to URL as fallback
      this.addToURL(handle, title, url)
    } catch (error) {
      console.warn('Could not store collection context:', error)
    }
  }

  // Retrieve collection context
  getContext() {
    try {
      // First try sessionStorage
      const stored = sessionStorage.getItem(this.storageKey)
      if (stored) {
        const context = JSON.parse(stored)
        if (this.isValid(context)) {
          return context
        } else {
          // Remove expired context
          sessionStorage.removeItem(this.storageKey)
        }
      }

      // Fallback to URL parameters
      return this.getFromURL()
    } catch (error) {
      console.warn('Error retrieving collection context:', error)
      return null
    }
  }

  // Check if context is still valid
  isValid(context) {
    if (!context || !context.timestamp) return false
    return Date.now() - context.timestamp < this.maxAge
  }

  // Clear context
  clearContext() {
    try {
      sessionStorage.removeItem(this.storageKey)
      this.removeFromURL()
    } catch (error) {
      console.warn('Could not clear collection context:', error)
    }
  }

  // Add collection info to URL parameters
  addToURL(handle, title, url) {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location)
      url.searchParams.set('from_collection', handle)
      url.searchParams.set('from_collection_title', encodeURIComponent(title))
      url.searchParams.set('from_collection_url', encodeURIComponent(url))
      window.history.replaceState({}, '', url)
    }
  }

  // Get collection info from URL parameters
  getFromURL() {
    const url = new URL(window.location)
    const handle = url.searchParams.get('from_collection')
    const title = url.searchParams.get('from_collection_title')
    const collectionUrl = url.searchParams.get('from_collection_url')

    if (handle && title && collectionUrl) {
      return {
        handle: handle,
        title: decodeURIComponent(title),
        url: decodeURIComponent(collectionUrl),
        timestamp: Date.now(),
      }
    }
    return null
  }

  // Remove collection info from URL parameters
  removeFromURL() {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location)
      url.searchParams.delete('from_collection')
      url.searchParams.delete('from_collection_title')
      url.searchParams.delete('from_collection_url')
      window.history.replaceState({}, '', url)
    }
  }

  // Update breadcrumb with collection context (without clearing it)
  updateBreadcrumb() {
    const context = this.getContext()
    if (!context) return

    // Skip shop-all since it's the same as "Shop"
    if (context.handle === 'shop') return

    const breadcrumbNav = document.querySelector('nav[aria-label="Breadcrumb"]')
    if (!breadcrumbNav) return

    const breadcrumbList = breadcrumbNav.querySelector('ol')
    if (!breadcrumbList) return

    // Check if collection breadcrumb already exists
    const existingCollectionItem = breadcrumbList.querySelector('[data-collection-context]')
    if (existingCollectionItem) return

    // Find the home link (first item)
    const homeItem = breadcrumbList.querySelector('li:first-child')
    if (!homeItem) return

    // Create collection breadcrumb item
    const collectionItem = document.createElement('li')
    collectionItem.className = 'flex items-center'
    collectionItem.setAttribute('data-collection-context', context.handle)

    const separator = breadcrumbNav.querySelector('.mx-2')?.textContent || '>'

    collectionItem.innerHTML = `
      <span class="mx-2">${separator}</span>
      <a href="${context.url}" class="underline-hover text-garnet title-espark transition-colors line-clamp-1 uppercase">
        ${context.title}
      </a>
    `

    // Insert collection item after home item
    homeItem.parentNode.insertBefore(collectionItem, homeItem.nextSibling)

    // Update JSON-LD schema to match visual breadcrumb
    this.updateSchema()

    // Context is NOT cleared here - it persists until page navigation
  }

  // Update JSON-LD BreadcrumbList schema to match the visual breadcrumb
  updateSchema() {
    const breadcrumbNav = document.querySelector('nav[aria-label="Breadcrumb"]')
    if (!breadcrumbNav) return

    const breadcrumbList = breadcrumbNav.querySelector('ol')
    if (!breadcrumbList) return

    const items = []
    const listItems = breadcrumbList.querySelectorAll(':scope > li')

    listItems.forEach((li, index) => {
      const link = li.querySelector('a')
      const span = li.querySelector('span:not(.mx-2)')
      const name = link
        ? link.textContent.trim()
        : span
          ? span.textContent.trim()
          : li.textContent.trim()
      const url = link ? link.href : window.location.href

      if (name) {
        items.push({
          '@type': 'ListItem',
          position: index + 1,
          name: name,
          item: url,
        })
      }
    })

    if (items.length === 0) return

    const newSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    }

    // Find and update the first BreadcrumbList schema, remove any duplicates
    const allScripts = document.querySelectorAll('script[type="application/ld+json"]')
    let updated = false
    for (const script of allScripts) {
      try {
        const parsed = JSON.parse(script.textContent)
        if (parsed['@type'] === 'BreadcrumbList') {
          if (!updated) {
            script.textContent = JSON.stringify(newSchema, null, 2)
            updated = true
          } else {
            script.remove()
          }
        }
      } catch (e) {
        // skip invalid JSON
      }
    }

    // If no existing BreadcrumbList found, create one
    if (!updated) {
      const newScript = document.createElement('script')
      newScript.type = 'application/ld+json'
      newScript.textContent = JSON.stringify(newSchema, null, 2)
      document.head.appendChild(newScript)
    }
  }
}

// Initialize global instance
window.collectionContextManager = new CollectionContextManager()

// Auto-clear context on certain pages
document.addEventListener('DOMContentLoaded', function () {
  const path = window.location.pathname

  // Clear context when navigating to collection pages or home
  if (
    (path.includes('/collections/') && !path.includes('/products/')) ||
    path === '/' ||
    path === '/shop'
  ) {
    window.collectionContextManager.clearContext()
  }

  // Update breadcrumb for product pages (adds collection context to visual breadcrumb)
  if (path.includes('/products/')) {
    window.collectionContextManager.updateBreadcrumb()
  }

  // Always sync JSON-LD schema with visual breadcrumb on ALL pages
  window.collectionContextManager.updateSchema()
})
